import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Button, Menu, MenuItem, useTheme } from '@mui/material';
import { JSX, MouseEvent, useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../core/config';
import { Loader } from '../../../ui/components';
import { useCustomersApiGet } from '../../hooks';
import { CustomerViewModel } from '../../model';

const SELECTED_CUSTOMER_ID_LOCAL_STORAGE_KEY = 'selectedCustomerId';

export const CustomerSelector = (): JSX.Element => {
  const theme = useTheme();
  const [menuAnchorElement, setMenuAnchorElement] = useState<null | HTMLElement>(null);
  const [customerName, setCustomerName] = useState<string | null>(null);
  const {customerId, setCustomerId} = useContext(AppContext);
  const {customers, isFetching, isFetched} = useCustomersApiGet();
  const isMenuOpened = Boolean(menuAnchorElement);
  const isButtonActive = Boolean(menuAnchorElement);
  const moreThanOneCustomerAccessible = customers.length > 1;

  const openMenu = (event: MouseEvent<HTMLButtonElement>): void => {
    if (moreThanOneCustomerAccessible) { 
      setMenuAnchorElement(event.currentTarget);
    }
  };

  const hideMenu = (): void => {
    setMenuAnchorElement(null);
  };

  const changeCustomer =  (customer: CustomerViewModel): void => {
    setCustomerId(customer.customerId);
    setCustomerName(customer.name);
    localStorage.setItem(SELECTED_CUSTOMER_ID_LOCAL_STORAGE_KEY, customer.customerId.toString());
    hideMenu();
  };

  useEffect(() => {
    if (isFetched && customers && customers.length > 0) {
      const storedCustomerId = localStorage.getItem(SELECTED_CUSTOMER_ID_LOCAL_STORAGE_KEY);
      let initialCustomer: CustomerViewModel | undefined;
      if (storedCustomerId) {
        initialCustomer = customers.find(customer => customer.customerId === parseInt(storedCustomerId, 10));
      }
      if (!initialCustomer) {
        initialCustomer = customers[0];
      }
      setCustomerId(initialCustomer.customerId);
      setCustomerName(initialCustomer.name);
    }
  }, [isFetched, customers, customerId, setCustomerId]);

  return <>
     { isFetching
      ? <Loader loading={isFetching} /> 
      : (isFetched && customers && (
        <Box sx={{ px: { sm: 1 }, color: isButtonActive ? theme.palette.primary.main : 'inherit'  }}>
          <Button
            role="button"
            variant="contained"
            onClick={openMenu}
            sx={{
              borderRadius: '0.5rem',
              justifyContent: 'flex-start',
              backgroundColor: 'transparent', 
              color: isButtonActive ? theme.palette.primary.main : 'inherit', 
              padding: '0.5rem 1rem',
              textTransform: 'none',
              boxShadow: 'none',
              border: 'none', 
              pointerEvents: moreThanOneCustomerAccessible ? 'auto' : 'none',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            }}
            startIcon= { <AccountCircleRoundedIcon /> }
            endIcon={ moreThanOneCustomerAccessible ? (isMenuOpened ? <ExpandLess /> : <ExpandMore />) : null }
          >
            {
              customers.length === 0 ? 'Brak uprawnień' :
              customerName === null ? 'Select a customer' :
              customerName
            }      
          </Button>
          { moreThanOneCustomerAccessible && (
            <Menu
              anchorEl={menuAnchorElement}
              open={isMenuOpened}
              onClose={hideMenu}
              MenuListProps={{ 'aria-labelledby': 'basic-button' }}
            >
              { customers.map(customer =>
                <MenuItem
                  onClick={ () => changeCustomer(customer)}
                  key={customer.customerId}
                >
                  <Box sx={{ ml: 2 }}>{customer.name}</Box>
                </MenuItem>)}
            </Menu>) }
        </Box>) ) }
    </>;
};